import { useState, useEffect, useMemo } from 'react';
import { getAssets } from '../api/assets';
import { generateIPRange, NETWORK_BASE, NETWORK_PREFIX } from '../data/networkData';

function buildKnownDevices(assets) {
  const map = {};
  assets.forEach(a => {
    if (a.ip_address) {
      map[a.ip_address] = {
        hostname:  a.hostname || a.ip_address,
        os:        a.os || '—',
        purpose:   a.device_type || '—',
        status:    a.status || 'unknown',
        type:      a.device_type === 'vm' ? 'Virtual' : 'Physical',
        scannable: a.scannable || 'scannable',
        id:        a.id,
      };
    }
  });
  return map;
}

function buildDeviceGroups(assets) {
  const groups = {};
  assets.forEach(a => {
    const type = a.device_type || 'other';
    if (!groups[type]) groups[type] = { id: type, label: labelFor(type), devices: [] };
    groups[type].devices.push({
      hostname: a.hostname || a.ip_address,
      ips:      [a.ip_address].filter(Boolean),
      lastSeen: a.last_seen ? new Date(a.last_seen).toLocaleString() : 'Unknown',
      type:     a.device_type === 'vm' ? 'Virtual' : 'Physical',
      offline:  a.status === 'offline',
      id:       a.id,
    });
  });
  return Object.values(groups);
}

function labelFor(type) {
  const labels = {
    workstation:  'Workstations',
    server:       'Servers',
    firewall:     'Firewalls',
    router:       'Routers',
    switch:       'Switches',
    hypervisor:   'Hypervisors',
    vm:           'Virtual Machines',
    taclane:      'TACLANE',
    idps:         'IDPS',
    vuln_scanner: 'Vulnerability Scanners',
    other:        'Other',
  };
  return labels[type] || type;
}

export function useAssets() {
  const [assets, setAssets]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    getAssets()
      .then(data => { setAssets(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  const knownDevices   = useMemo(() => buildKnownDevices(assets), [assets]);
  const deviceGroups   = useMemo(() => buildDeviceGroups(assets), [assets]);
  const ipRange        = useMemo(() => generateIPRange(NETWORK_BASE, NETWORK_PREFIX, knownDevices), [knownDevices]);

  const summary = useMemo(() => {
    const unique = assets.filter((a, i, arr) => arr.findIndex(b => b.hostname === a.hostname) === i);
    return {
      totalIPs:     ipRange.length,
      assigned:     ipRange.filter(i => i.status !== 'unassigned').length,
      online:       unique.filter(a => a.status === 'online').length,
      offline:      unique.filter(a => a.status === 'offline').length,
      unassigned:   ipRange.filter(i => i.status === 'unassigned').length,
      totalDevices: unique.length,
    };
  }, [assets, ipRange]);

  return { assets, knownDevices, deviceGroups, ipRange, summary, loading, error, setAssets };
}
